import DriverAccordion from './driver_accordion.tsx';
import './driver_list.css';
import Spinner from '../../common/spinner/spinner.tsx';

import { useInfiniteQuery } from '@tanstack/react-query';
import { Driver, SensorDriverPackage } from '../../../types/driver_types.ts';
import { useEffect, useMemo, useState } from 'react';
import Fuse from 'fuse.js';
import FilteringPanel, {
    Filters,
} from '../../common/filtering_panel/filtering_panel.tsx';
import { useAzureAuth } from '../../../hooks/use_azure_auth.ts';
import { useSearchParams } from 'react-router';
import { URL_SEARCH_PARAM } from '../../../config/constants.ts';
import { fetchDriverList } from '../../../utils/di_api_functions.ts';
import { parseDriverList } from '../../../utils/utils.ts';

const exactMatchThreshold = 1e-3;
const searchSeparators = ['-'];

const fuseSearchOptions = {
    includeScore: true,
    threshold: 0.2,
    keys: ['sensorName'],
};

const ErrorPlaceholder = (props: { error: Error }) => {
    return (
        <div className="driver_list_placeholder_area">
            <h4>Error</h4>
            {props.error.message}
        </div>
    );
};

const SpinnerPlaceholder = (props: { text: string }) => {
    return (
        <div className="driver_list_placeholder_area">
            <Spinner />
            <span>{props.text}</span>
        </div>
    );
};

const NoDataPlaceholder = () => {
    return (
        <div className="driver_list_placeholder_area">
            <h4>No drivers found</h4>
        </div>
    );
};

const DriverList = () => {
    const user = useAzureAuth();
    const showReleaseStateColumn = Boolean(user.userProps.token);

    const {
        isPending,
        isError,
        data,
        error,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
    } = useInfiniteQuery({
        queryKey: ['driver_list', user.userProps.token],
        queryFn: ({ pageParam }) =>
            fetchDriverList(user.userProps.token, pageParam),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: lastPage => lastPage.next || undefined,
    });

    useEffect(() => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage().catch(err =>
                console.log(`Error while fetching drivers: ${err}`),
            );
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    const [searchParams, setSearchParams] = useSearchParams();
    const [search, setSearch] = useState(
        searchParams.get(URL_SEARCH_PARAM) || '',
    );

    useEffect(() => {
        if (search) {
            setSearchParams({ [URL_SEARCH_PARAM]: search }, { replace: true });
        } else {
            setSearchParams({}, { replace: true });
        }
    }, [search, setSearchParams]);

    const [filters, setFilters] = useState<Filters>({
        release_state: user.userProps.token ? 'all' : 'PUB',
        sw_target: 'all',
        interface: 'all',
        show_deprecated: false,
    });

    const [allDriverPackages, setAllDriverPackages] = useState<
        SensorDriverPackage[]
    >([]);
    const [filteredDriverList, setFilteredDriverList] = useState<
        SensorDriverPackage[]
    >([]);
    const [displayDriverList, setDisplayDriverList] = useState<
        SensorDriverPackage[]
    >([]);

    const fuse = useMemo(
        () => new Fuse<SensorDriverPackage>([], fuseSearchOptions),
        [],
    );

    // On data loaded, fill list with complete data
    useEffect(() => {
        if (data) {
            const allDrivers: Driver[] = data.pages.flatMap(
                page => page.results,
            );
            parseDriverList(allDrivers).then(parsedData => {
                setAllDriverPackages([...parsedData.values()]);
            });
        }
    }, [data]);

    useEffect(() => {
        // Filtering the driver list on filter updates (and source data update)
        let filteredList: SensorDriverPackage[] = allDriverPackages.map(
            driver_package => {
                const filtered_package = { ...driver_package };
                for (const [filter_name, filter_value] of Object.entries(
                    filters,
                )) {
                    if (filter_value === 'all') continue;

                    switch (filter_name) {
                        case 'sw_target': {
                            filtered_package.drivers =
                                filtered_package.drivers.filter(
                                    driver =>
                                        driver.sw_target.name === filter_value,
                                );
                            break;
                        }
                        case 'interface': {
                            filtered_package.drivers =
                                filtered_package.drivers.filter(
                                    driver =>
                                        driver.interface.name === filter_value,
                                );
                            break;
                        }
                        case 'release_state': {
                            // check if deprecated should be kept when filtering for PUBLIC drivers
                            if (
                                filter_value == 'PUB' &&
                                filters['show_deprecated']
                            ) {
                                filtered_package.drivers =
                                    filtered_package.drivers.filter(
                                        driver =>
                                            driver.release_state ===
                                                filter_value ||
                                            driver.release_state === 'DPR',
                                    );
                            } else {
                                filtered_package.drivers =
                                    filtered_package.drivers.filter(
                                        driver =>
                                            driver.release_state ===
                                            filter_value,
                                    );
                            }

                            break;
                        }
                        case 'show_deprecated': {
                            if (!filter_value) {
                                filtered_package.drivers =
                                    filtered_package.drivers.filter(
                                        driver => driver.release_state != 'DPR',
                                    );
                            }
                            break;
                        }
                        default: {
                            filtered_package.drivers =
                                filtered_package.drivers.filter(
                                    driver =>
                                        driver[filter_name as never] ===
                                        filter_value,
                                );
                            break;
                        }
                    }
                }
                return filtered_package;
            },
        );
        filteredList = filteredList.filter(
            driver_package => driver_package.drivers.length > 0,
        );
        setFilteredDriverList(filteredList);
        fuse.setCollection(filteredList);
    }, [filters, allDriverPackages, fuse]);

    useEffect(() => {
        //  Update displayed list on filter update or search change
        if (search === '') {
            setDisplayDriverList(filteredDriverList);
            return;
        }

        // Search for product references separators in search string
        for (const sep of searchSeparators) {
            if (search.includes(sep)) {
                const prefix = search.split(sep, 1)[0];
                const results = fuse.search(prefix);
                // Return only one entry if exact match on sensor model prefix
                if (
                    results.length > 0 &&
                    results[0].score! < exactMatchThreshold
                ) {
                    setDisplayDriverList([results[0].item]);
                    return;
                }
                // Return list of results if search has no exact match
                setDisplayDriverList(results.map(result => result.item));
                return;
            }
        }

        // Run normal search
        const results = fuse.search(search);

        // Return only one entry in case of exact match
        if (results.length > 0 && results[0].score! < exactMatchThreshold) {
            setDisplayDriverList([results[0].item]);
            return;
        }
        setDisplayDriverList(results.map(result => result.item));
    }, [search, filteredDriverList, fuse]);

    if (isPending && !data) {
        // No data is loaded yet
        return (
            <>
                <FilteringPanel
                    onSearchChangeCallback={setSearch}
                    filters={filters}
                    setFilters={setFilters}
                    searchValue={search}
                />
                <SpinnerPlaceholder text="Loading driver data..." />
            </>
        );
    }

    if (isError) {
        // An error occurred while loading data
        return (
            <>
                <FilteringPanel
                    onSearchChangeCallback={setSearch}
                    filters={filters}
                    setFilters={setFilters}
                    searchValue={search}
                />
                <ErrorPlaceholder error={error} />
            </>
        );
    }

    if (filteredDriverList.length === 0 && !hasNextPage) {
        return (
            <>
                <FilteringPanel
                    onSearchChangeCallback={setSearch}
                    filters={filters}
                    setFilters={setFilters}
                    searchValue={search}
                />
                <NoDataPlaceholder />
            </>
        );
    }

    return (
        <>
            <FilteringPanel
                onSearchChangeCallback={setSearch}
                filters={filters}
                setFilters={setFilters}
                searchValue={search}
            />
            <div className="driver_list__container">
                <div className="driver_list__header">
                    <div>Name</div>
                    <div># of drivers</div>
                    {showReleaseStateColumn && <div>State</div>}
                    <div></div>
                </div>
                {displayDriverList.map(
                    (driverPackage: SensorDriverPackage, idx: number) => {
                        const open = displayDriverList.length === 1;
                        return (
                            <div
                                key={`${idx}-${open ? 'open' : 'closed'}`}
                                className="driver_list_element"
                            >
                                <DriverAccordion
                                    driverPackage={driverPackage}
                                    open={open}
                                />
                            </div>
                        );
                    },
                )}
            </div>
            {displayDriverList.length === 0 && !hasNextPage ? (
                <NoDataPlaceholder />
            ) : (
                ''
            )}
            {hasNextPage ? (
                <SpinnerPlaceholder text="Loading driver data..." />
            ) : (
                ''
            )}
        </>
    );
};

export default DriverList;
